<template>
  <!-- Table -->
  <DataView
    ref="dataViewRef"
    v-bind="tableConfigRef"
    :class="{
      'from-user-has-reports': fromUserHasReports || fromUserGroupHasReports,
    }"
  >
    <template
      v-if="!fromUserHasReports && !fromUserGroupHasReports"
      #header__before
    >
      <div class="flex gap-2">
        <SplitButton
          @click="handleOpenOptions()"
          class="add-button"
          label="Añadir"
          :model="items"
        />
        <Button
          v-if="false"
          class="download-button"
          icon="pi pi-download"
          @click="handleDownloadExcel()"
        />
      </div>
    </template>

    <!-- AliasCategoria column -->
    <template #table__AliasCategoria__body="{ data }">
      <div class="long-text-column">
        {{ data.categoria }}
      </div>
    </template>
    <template #list__AliasCategoria="{ data }">
      <div class="long-text-column">
        {{ data.categoria }}
      </div>
    </template>

    <!-- descripcion column -->
    <template #table__descripcion__body="{ data }">
      <div v-if="data.descripcion" class="long-text-column">
        {{ data.descripcion }}
      </div>
      <div v-else class="text-gray-400">-</div>
    </template>
    <template #list__descripcion="{ data }">
      <div v-if="data.descripcion" class="long-text-column">
        {{ data.descripcion }}
      </div>
      <div v-else class="text-gray-400">-</div>
    </template>

    <!-- custom Url content -->
    <template #table__Url__body="{ data }">
      <div class="text-primary cursor-pointer" @click="goReport(data)">
        {{ data.alias }}
      </div>
    </template>
    <template #list__Url="{ data }">
      <div class="text-primary cursor-pointer" @click="goReport(data)">
        {{ data.alias }}
      </div>
    </template>
  </DataView>

  <!-- Modal -->
  <ReportsDialogView ref="modalRef" @saved="updateTableList" />

  <!-- Report has users modal -->
  <ReportHasUsersDialogView ref="reportHasUsersModalRef" />

  <CopyUsersAndGroupsDialogView
    ref="copyUsersAndGroupsModalRef"
  />
</template>

<script setup>
import { ref } from "vue";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import SplitButton from "primevue/splitbutton";
import Button from "primevue/button";
import { DataView, useImmer } from "@sttedra_ui/framework";

import useTableConfig from "@/pages/_root/reports/reports-table.view-config.js";
import ReportsDialogView from "@/pages/_root/reports-dialog/ReportsDialogView.vue";
import ReportHasUsersDialogView from "@/pages/_root/report-has-users-dialog/ReportHasUsersDialogView.vue"; // eslint-disable-line
import {
  reportsDelete,
  userReportDetails,
  userReportAdditionalDetails,
} from "@/api/reports/reports.api.js";
import {
  convertAliasToURL,
  showLoadingWhenDeletingRegister,
} from "@/composables/_commons/helper.js";
import { reportTypes } from "@/composables/_commons/enums.js";
import CopyUsersAndGroupsDialogView 
  from "../copy-users-and-groups-dialog/CopyUsersAndGroupsDialogView.vue";
import { tipoClonacion } from "@/composables/_commons/enums.js";

// #region Props, Emits
const props = defineProps({
  fromUserHasReports: {
    type: Number,
    default: null,
  },
  fromUserGroupHasReports: {
    type: Number,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const confirm = useConfirm();
const toast = useToast();
const modalRef = ref(null);
const copyUsersAndGroupsModalRef = ref(false);
const reportHasUsersModalRef = ref(null);
const dataViewRef = ref(null);
const tableConfig = new useTableConfig(
  props.fromUserHasReports,
  props.fromUserGroupHasReports,
  buildModal,
  deleteRegister,
);
const { ref: tableConfigRef } = useImmer(tableConfig);
const items = [
  {
    label: "Informe PowerBI",
    icon: "pi pi-file",
    command: () => {
      buildModal(null, reportTypes.PowerBI);
    },
  },
  {
    label: "Tabla",
    icon: "pi pi-database",
    command: () => {
      buildModal(null, reportTypes.Tabla);
    },
  },
  {
    label: "Gráfico",
    icon: "pi pi-chart-bar",
    disabled: true,
    command: () => {
      buildModal(null, reportTypes.Grafico);
    },
  },
  {
    label: "Mapa",
    icon: "pi pi-map",
    disabled: true,
    command: () => {
      buildModal(null, reportTypes.Mapa);
    },
  },
  {
    label: "URL",
    icon: "pi pi-link",
    command: () => {
      buildModal(null, reportTypes.URL);
    },
  },
];
// #endregion

// #region Functions
async function buildModal(event = null, type = reportTypes.PowerBI) {
  let data = event ? event.data : null;

  let reportDetails = null;
  let additionalDetails = null;

  if (data) {
    let response = await userReportDetails(data.alias);

    if (response.status != 200) {
      return;
    }

    reportDetails = response.data;
    additionalDetails = await userReportAdditionalDetails(data.alias);
  }

  data = { ...reportDetails, ...additionalDetails };

  if (type == "users") {
    reportHasUsersModalRef.value.showModal(data);
  } 
  else if (type == "copy_users_groups") {
    copyUsersAndGroupsModalRef.value.showModal(data.alias, tipoClonacion.Both, true);
  }
  else {
    modalRef.value.getAditionalData(data, type);
  }
}

function deleteRegister(event) {
  const data = event.data;

  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      let { deleteActionButton, deleteActionButtonHtml } =
        showLoadingWhenDeletingRegister(event.originalEvent.target);

      const response = await reportsDelete(data.id);

      if (response.status == 200) {
        toast.add({
          severity: "success",
          summary: "Registro borrado",
          detail: `Se ha borrado el registro correctamente`,
          life: 3000,
        });

        updateTableList();
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Ha ocurrido un error al borrar el registro",
          life: 3000,
        });
      }

      deleteActionButton.innerHTML = deleteActionButtonHtml;
    },
  });
}

function updateTableList() {
  dataViewRef.value.debounceRefreshAndUpdate();
}

function goReport(data) {
  // Open new tab with the report
  window.open(convertAliasToURL(data.alias, data.esPublico, false), "_blank");
}

function handleOpenOptions() {
  const addButton = document.querySelector(".add-button");

  if (addButton) {
    const splittedButton = addButton.querySelector(".p-splitbutton-menubutton");

    if (splittedButton) {
      splittedButton.click();
    }
  }
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
.add-button {
  :deep(.p-button) {
    padding: 0.75rem;
    max-height: 2.5rem;

    .p-button-label {
      font-weight: normal;
    }
  }
}

.download-button {
  padding: 0.75rem;
  max-height: 2.5rem;
}

.from-user-has-reports {
  :deep(.smart-content__center) {
    padding: 0;
  }
}
</style>
